<script setup lang="ts">
import { reactive, ref } from 'vue'
// Registra el web component <altcha-widget> (captcha proof-of-work).
// El script se sirve desde nuestro propio bundle: no hay CDN ni terceros.
import 'altcha'
import { contactChannels, socialLinks } from '../data/landing.content'
import { useLandingScroll } from '../composables/useLandingScroll'
import { sendContactMessage } from '../services/contact.service'

const { registerReveal } = useLandingScroll()

const form = reactive({
  nombre: '',
  email: '',
  asunto: '',
  mensaje: '',
  // Campo trampa (honeypot): oculto a las personas, lo llenan los bots.
  website: ''
})

const submitted = ref(false)
const sending = ref(false)
const errorMessage = ref('')

/** Widget del captcha ALTCHA (verificado con un reto de proof-of-work). */
interface AltchaWidgetElement extends HTMLElement {
  verify?: (options?: Record<string, unknown>) => Promise<{ payload: string } | null>
}

const captchaWidget = ref<AltchaWidgetElement | null>(null)

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function resetForm() {
  form.nombre = ''
  form.email = ''
  form.asunto = ''
  form.mensaje = ''
  form.website = ''
}

function validate(): string {
  if (!form.nombre.trim()) return 'Escribe tu nombre para que podamos dirigirnos a ti.'
  if (!EMAIL_PATTERN.test(form.email.trim())) return 'Escribe un correo electrónico válido.'
  if (!form.asunto) return 'Selecciona un tema.'
  if (form.mensaje.trim().length < 10) return 'Cuéntanos un poco más (mínimo 10 caracteres).'
  return ''
}

/**
 * Resuelve el reto del captcha y devuelve el payload firmado.
 *
 * El backend es fail-closed: sin payload valido no envia el mensaje. Si el
 * reto no se puede resolver (navegador sin WebAssembly, contexto no seguro,
 * backend sin reto) no se envia nada y se avisa a la persona.
 */
async function resolveCaptcha(): Promise<string> {
  const widget = captchaWidget.value

  if (!widget?.verify) return ''

  try {
    const result = await widget.verify()
    return result?.payload ?? ''
  } catch {
    return ''
  }
}

async function submit() {
  if (sending.value) return

  errorMessage.value = ''

  const invalid = validate()
  if (invalid) {
    errorMessage.value = invalid
    return
  }

  sending.value = true

  try {
    const altcha = await resolveCaptcha()

    if (!altcha) {
      errorMessage.value =
        'No pudimos verificar tu navegador. Revisa que no tengas bloqueados los scripts y vuelve a intentarlo.'
      return
    }

    const result = await sendContactMessage({
      nombre: form.nombre,
      email: form.email,
      asunto: form.asunto,
      mensaje: form.mensaje,
      website: form.website,
      altcha
    })

    submitted.value = result.success
    resetForm()
  } catch (error) {
    errorMessage.value =
      (error as Error)?.message || 'No fue posible enviar el mensaje. Intenta de nuevo.'
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <section class="wl-contacto-section" id="contacto">
    <div class="container">
      <div class="section-head reveal" :ref="registerReveal">
        <div class="section-tag">Contacto</div>
        <h2 class="section-title">¿Tienes alguna <span class="gradient-text">duda?</span></h2>
        <p class="section-desc">Estamos aquí para ayudarte. Escríbenos y te respondemos en menos de 24 horas.</p>
      </div>

      <div class="wl-contacto-grid">
        <div class="wl-contacto-form-wrap reveal" :ref="registerReveal">
          <form class="wl-contacto-form" novalidate @submit.prevent="submit">
            <div class="wl-form-row-2">
              <div class="wl-form-group">
                <label for="wl-cnombre">Nombre</label>
                <input id="wl-cnombre" v-model="form.nombre" type="text" name="nombre" placeholder="Tu nombre completo" required />
              </div>
              <div class="wl-form-group">
                <label for="wl-cemail">Correo electrónico</label>
                <input id="wl-cemail" v-model="form.email" type="email" name="email" placeholder="tu@correo.com" required />
              </div>
            </div>

            <div class="wl-form-group">
              <label for="wl-casunto">Asunto</label>
              <select id="wl-casunto" v-model="form.asunto" name="asunto" required>
                <option value="" disabled>Selecciona un tema...</option>
                <option value="soporte">Soporte técnico</option>
                <option value="ventas">Información sobre planes</option>
                <option value="contenido">Preguntas sobre contenido</option>
                <option value="prensa">Prensa y medios</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div class="wl-form-group">
              <label for="wl-cmensaje">Mensaje</label>
              <textarea
                id="wl-cmensaje"
                v-model="form.mensaje"
                name="mensaje"
                rows="5"
                placeholder="Cuéntanos cómo podemos ayudarte..."
                required
              ></textarea>
            </div>

            <!--
              Captcha ALTCHA: reto de proof-of-work resuelto en el navegador.
              Es invisible (no molesta a la persona) pero el backend exige su
              payload: sin el, el mensaje no se envia.
            -->
            <altcha-widget
              ref="captchaWidget"
              challenge="/altcha/challenge"
              display="invisible"
              name="altcha"
            ></altcha-widget>

            <!-- Campo trampa: invisible para la persona, util contra bots. -->
            <div class="wl-form-hp" aria-hidden="true">
              <label for="wl-cwebsite">No completes este campo</label>
              <input
                id="wl-cwebsite"
                v-model="form.website"
                type="text"
                name="website"
                tabindex="-1"
                autocomplete="off"
              />
            </div>

            <button type="submit" class="btn btn-primary btn-full wl-form-submit-btn" :disabled="sending">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {{ sending ? 'Enviando…' : 'Enviar mensaje' }}
            </button>

            <div class="wl-form-success" :class="{ 'wl-is-show': submitted }">
              <div class="wl-fs-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <path d="M22 4L12 14.01l-3-3" />
                </svg>
              </div>
              <p><strong>¡Mensaje enviado!</strong> Te responderemos pronto.</p>
            </div>

            <div class="wl-form-error" :class="{ 'wl-is-show': !!errorMessage }">
              <div class="wl-fs-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>{{ errorMessage }}</p>
            </div>
          </form>
        </div>

        <div class="wl-contacto-info reveal reveal-d1" :ref="registerReveal">
          <div class="wl-cinfo-card">
            <div v-for="channel in contactChannels" :key="channel.title" class="wl-cinfo-item">
              <div class="wl-cinfo-icon" :class="{ 'wl-cinfo-wa': channel.whatsapp }">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path v-for="(path, i) in channel.icon.split('|')" :key="i" :d="path" />
                </svg>
              </div>
              <div>
                <h4>{{ channel.title }}</h4>
                <a v-if="channel.href" :href="channel.href" target="_blank" rel="noopener">{{ channel.value }}</a>
                <span v-else>{{ channel.value }}</span>
              </div>
            </div>
          </div>

          <div class="wl-cinfo-social">
            <p>Síguenos en redes sociales</p>
            <div class="wl-cinfo-links">
              <a
                v-for="social in socialLinks"
                :key="social.label"
                :href="social.href"
                target="_blank"
                rel="noopener"
                class="wl-clink-btn"
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  :fill="social.filled ? 'currentColor' : 'none'"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path v-for="(path, i) in social.icon.split('|')" :key="i" :d="path" />
                </svg>
                {{ social.label }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
